import React from 'react';
import { Check, Calendar as CalendarIcon } from 'lucide-react';
import { LeadTime, ScheduledLeadTime, Product } from '../models/DeliveryOptionDetailsModel';

interface ProductLeadTimeSectionProps {
  product: Product;
}

const LeadTimeBreakdownTable = ({ breakdownData }: { breakdownData: LeadTime}) => {
  const baseVal = breakdownData?.LeadTime ?? breakdownData?.LeadTimeValue ?? 0;
  const optionList = breakdownData?.OptionLeadTimes || breakdownData?.ProductOptionLeadTimes || [];
  const optionsTotal = optionList.reduce((acc: number, cur: any) => acc + (cur.LeadTime ?? cur.LeadTimeValue ?? 0), 0);
  const finalTotal = (baseVal + optionsTotal);

  return (
    <table className="w-full text-[10px] border-separate border-spacing-y-0.5" style={{ tableLayout: 'fixed' }}>
      <thead>
        <tr className="text-slate-400 font-bold uppercase text-[9px]">
          <th className="text-left pb-1" style={{ width: '160px' }}>Component</th>
          <th className="text-right pb-1">Days</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-t border-slate-100">
          <td className="py-1 font-medium text-slate-600 italic truncate" style={{ width: '160px' }}>Base Lead Time</td>
          <td className="py-1 text-right font-black text-black">{baseVal}</td>
        </tr>
        {optionList?.map((optLT: any, idx: number) => {
          const name = optLT.ProductOptionName || optLT.Option || "Option";
          const val = optLT.ProductOptionValue || optLT.OptionValue || "";
          const fullName = val ? `${name}:${val}` : name;
          const itemVal = optLT.LeadTime ?? optLT.LeadTimeValue ?? 0;
          return (
            <tr key={idx} className="border-t border-slate-50 text-black">
              <td className="py-1 text-slate-500 font-mono truncate" title={fullName}>{fullName}</td>
              <td className="py-1 text-right font-black text-black">+{itemVal}</td>
            </tr>
          );
        })}
        <tr className="border-t-2 border-teal-500 pt-1">
          <td className="font-black text-teal-700 uppercase pt-1 truncate">Total</td>
          <td className="text-right font-black text-teal-700 pt-1">{finalTotal}</td>
        </tr>
      </tbody>
    </table>
  );
};

const ProductLeadTimeSection: React.FC<ProductLeadTimeSectionProps> = ({ product }) => {
  const tag = product.Tag;
  let activeLeadTime:LeadTime | null = product.ProductLeadTime.LeadTimeBreakdown;
  const schedules = product?.ProductLeadTime?.RawLeadTime?.ScheduledLeadTimes || [];
  const baseLeadTime = product.ProductLeadTime.RawLeadTime;  
  const activeSchedule = schedules.find(sched => sched.Available);
  if(!activeSchedule) {
    activeLeadTime = null;
  }  
  
  // The requested highlighted style
  const highlightedStyle = {
    backgroundColor: '#f0f7ff',
    border: '2px solid #b3d7ff'
  };
  return (
    <div className="flex flex-col gap-2">
          <div key={tag} className="rounded-lg border-2 border-teal-200 overflow-hidden bg-white shadow-sm">
            
            {/* Collapsible Content */}
            <div className="px-3 py-3 border-t border-teal-100 bg-teal-50/30 text-black">
                <div className="flex flex-row flex-nowrap overflow-x-auto gap-4 pb-2 custom-scrollbar w-full" style={{ display: 'flex', overflowX: 'auto' }}>
                  
                   {/* Base Lead Time Column */}
                   <div style={{ flexShrink: 0, borderRadius: '6px', padding: '8px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', display: 'flex', flexDirection: 'column', minWidth: '230px', width: '230px',...(activeLeadTime === null ? highlightedStyle : { border: '1px solid #e2e8f0' }) }}>
                    <div style={{ fontSize: '9px', fontWeight: 900, color: '#0056b3', textTransform: 'uppercase', borderBottom: '1px solid #e1efff', marginBottom: '4px', paddingBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>Base Lead Time</span>{!activeLeadTime && <Check size={10} strokeWidth={3} />}
                    </div>
                    {schedules.length > 0 && (
                      <div className="mb-2 invisible select-none pointer-events-none">
                        <table className="w-full text-[8px] font-mono border-collapse">
                          <tbody>
                            <tr><td className="w-[30px] pr-1">&nbsp;</td><td>&nbsp;</td></tr>
                            <tr><td className="w-[30px] pr-1">&nbsp;</td><td>&nbsp;</td></tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="mb-2 h-8" />
                    {baseLeadTime ? <LeadTimeBreakdownTable breakdownData={baseLeadTime} /> : <div className="text-[10px] italic py-2 text-slate-400">Trace not found</div>}
                  </div>

                  {/* Active Lead Time Column */}
                  {activeLeadTime && (
                  <div style={{ flexShrink: 0, ...highlightedStyle, borderRadius: '6px', padding: '8px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', display: 'flex', flexDirection: 'column', minWidth: '230px', width: '230px' }}>
                    <div style={{ fontSize: '9px', fontWeight: 900, color: '#0056b3', textTransform: 'uppercase', borderBottom: '1px solid #e1efff', marginBottom: '4px', paddingBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>Active Lead Time</span><Check size={10} strokeWidth={3} />
                    </div>
                    {schedules.length > 0 && (
                      <div className="mb-2 invisible select-none pointer-events-none">
                        <table className="w-full text-[8px] font-mono border-collapse">
                          <tbody>
                            <tr><td className="w-[30px] pr-1">&nbsp;</td><td>&nbsp;</td></tr>
                            <tr><td className="w-[30px] pr-1">&nbsp;</td><td>&nbsp;</td></tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="mb-2 h-8" />
                    {activeLeadTime ? <LeadTimeBreakdownTable breakdownData={activeLeadTime} /> : <div className="text-[10px] italic py-2 text-slate-400">Trace not found</div>}
                  </div>)}

                  {/* Scheduled Lead Times Columns */}
                  {schedules.map((sched: ScheduledLeadTime, sIdx: number) => (
                    <div key={sIdx} className="shrink-0 bg-amber-50 border-2 border-amber-200 rounded-md p-2 shadow-sm flex flex-col" style={{ minWidth: '230px', width: '230px',...(sched.Available ? highlightedStyle : { border: '1px solid #e2e8f0' }) }}>
                      <div style={{float: 'right'}} title={sched.StartDate + "\r\n" + sched.EndDate}>
                        <CalendarIcon size={20} className="text-teal-600 shrink-0" />
                      </div>
                      <div className="text-[9px] font-black text-amber-700 uppercase border-b border-amber-100 mb-1 pb-1 truncate" title={sched.Name}>
                        {sched.Name || "Schedule"}
                      </div>
                      <table className="w-full text-[8px] mb-2 font-mono border-collapse">
                        <tbody>
                        <tr><td className="w-[30px] font-bold uppercase text-amber-800 pr-1">From:</td><td className="text-amber-900/70">{sched.StartDate?.split('T')[0]} {sched.StartDate?.split('T')[1]?.substring(0, 9)}</td></tr>
                        <tr><td className="w-[30px] font-bold uppercase text-amber-800 pr-1">To</td><td className="text-amber-900/70">{sched.EndDate?.split('T')[0]} {sched.EndDate?.split('T')[1]?.substring(0, 9)}</td></tr>
                        </tbody>
                      </table>
                      <LeadTimeBreakdownTable breakdownData={sched} />
                    </div>
                  ))}
                </div>
              </div>
          </div>
    </div>
  );
};

export default ProductLeadTimeSection;